import { useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { AIFeature } from 'enums';
import { useMatchNavigation, useSearchTeamInsuranceProduct } from 'hooks';
import { QuoteFormRoutes } from 'broker/broker-routes';
import { useGetInsuranceProductsFromTeamProducts } from 'broker/hooks';
import { AIContextProvider } from 'broker/pages/SubmissionWorkspacePage/contexts/ai/AIContextProvider';
import { useUiStoreState } from 'broker/pages/SubmissionWorkspacePage/ui-store/uiStoreProvider';
import { useFileWithMetadata } from './components/formSteps/Upload/useFileWithMetadata';
import { getQuoteConfig } from './config';
import { useQuoteFormMethods } from './form-methods/useQuoteFormMethods';
import QuoteContent, { QuoteContentProps } from './QuoteContent';

export default function QuoteMethodsProvider(
  props: Omit<
    QuoteContentProps,
    | 'appliedSuggestions'
    | 'initialProduct'
    | 'initialProductId'
    | 'quoteConfig'
    | 'currentStep'
    | 'selectedFiles'
    | 'teamProductsLoading'
    | 'products'
  >,
) {
  const { locationState, setIsDirty, fileType } = props;

  const fileWithExtractionMetadata = useFileWithMetadata(fileType, true);

  // get the fileWithExtractionMetadata according to the quote mode (market quote look for the first file with market quote label, policy look for the first file with market policy label)
  const currentStep = useMatchNavigation(QuoteFormRoutes, QuoteFormRoutes.Details);

  const {
    filesExplorer: { selectedFiles },
  } = useUiStoreState();

  const quoteConfig = useMemo(
    () =>
      getQuoteConfig({
        fileType,
        formMode: locationState.mode,
        fileWithExtractionMetadata,
        currentStep,
        selectedFiles,
      }),
    [fileType, locationState.mode, fileWithExtractionMetadata, currentStep, selectedFiles],
  );

  const { items: teamProducts, isLoading: teamProductsLoading } = useSearchTeamInsuranceProduct();

  const products = useGetInsuranceProductsFromTeamProducts(teamProducts);

  const initialProductId = locationState?.insuranceProductId ?? locationState?.selectedQuote?.insuranceProduct?.id;
  const initialProduct = products.find((product) => product.id === initialProductId);
  const { methods, appliedSuggestions } = useQuoteFormMethods(
    locationState.selectedQuote,
    setIsDirty,
    initialProduct,
    locationState.mode,
    quoteConfig.suggestedValueProps,
  );

  return (
    <AIContextProvider feature={AIFeature.QuoteGeneration}>
      <FormProvider {...methods}>
        <QuoteContent
          {...props}
          selectedFiles={selectedFiles}
          initialProductId={initialProductId}
          appliedSuggestions={appliedSuggestions}
          teamProductsLoading={teamProductsLoading}
          products={products}
          quoteConfig={quoteConfig}
          currentStep={currentStep}
          fileType={fileType}
        />
      </FormProvider>
    </AIContextProvider>
  );
}
